import { useEffect, useRef, useState } from 'react'
import { cn } from '#app/utils/misc.tsx'
import {
	getMuxHlsUrl,
	getMuxPosterUrl,
	useDeferredVideoMount,
} from './deferred-video-layer.tsx'

interface VideoAsImageProps {
	muxPlaybackId: string
	posterUrl?: string
	className?: string
}

/**
 * Autoplay video styled to look like an image. No controls, no chrome —
 * muted, looped, playsInline. Plays when scrolled into view.
 *
 * The poster image is painted as a CSS background on the host element
 * BEFORE the <video> mounts, so something is visible during page load
 * and the LCP candidate isn't disrupted. The <video> element itself
 * only mounts after window.load + viewport intersection (and never if
 * prefers-reduced-motion is set).
 */
export function VideoAsImage({
	muxPlaybackId,
	posterUrl,
	className,
}: VideoAsImageProps) {
	const { hostRef, shouldMount, inView } = useDeferredVideoMount<HTMLDivElement>(
		{ skipOnReducedMotion: true },
	)
	const videoRef = useRef<HTMLVideoElement | null>(null)
	const [isPlaying, setIsPlaying] = useState(false)
	const poster = posterUrl || getMuxPosterUrl(muxPlaybackId, 1200)

	useEffect(() => {
		const video = videoRef.current
		if (!video) return
		if (inView) {
			void video.play().catch(() => {})
		} else {
			video.pause()
		}
	}, [inView, shouldMount])

	return (
		<div
			ref={hostRef}
			className={cn('relative h-full w-full overflow-hidden', className)}
			style={{
				backgroundImage: `url(${poster})`,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
			}}
		>
			{shouldMount ? (
				<video
					ref={videoRef}
					src={getMuxHlsUrl(muxPlaybackId)}
					poster={poster}
					muted
					loop
					playsInline
					preload="metadata"
					onPlaying={() => setIsPlaying(true)}
					onPause={() => setIsPlaying(false)}
					className={cn(
						'h-full w-full object-cover transition-opacity duration-300',
						isPlaying ? 'opacity-100' : 'opacity-0',
					)}
				/>
			) : null}
		</div>
	)
}
